import { PaymentMethod } from "@core/invoices/types";
import { trans } from "@mongez/localization";
import { FaCheck } from "react-icons/fa";

type PaymentMethodCardProps = {
  method: PaymentMethod;
  config: {
    name: string;
    description: string;
    icon: React.ComponentType<{ size?: number }>;
    image: string;
    color: string;
  };
  isSelected: boolean;
  onSelect: (method: PaymentMethod) => void;
};

export const PaymentMethodCard = ({
  method,
  config,
  isSelected,
  onSelect,
}: PaymentMethodCardProps) => {
  const IconComponent = config.icon;
  return (
    <button
      onClick={() => onSelect(method)}
      className={`group relative overflow-hidden rounded-2xl transition-all duration-300 w-full h-40 ${
        isSelected
          ? "bg-gradient-to-br from-primary via-primary/90 to-primary/80 shadow-xl shadow-primary/25 scale-105"
          : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:shadow-gray-200/50 dark:hover:shadow-gray-900/50 hover:scale-102 hover:border-primary/30 dark:hover:border-primary/40"
      }`}
    >
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      
      {/* Selection Glow Effect */}
      {isSelected && (
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent animate-pulse"></div>
      )}

      <div className="relative flex h-full flex-col items-center justify-center px-4 py-6 space-y-3">
        {/* Top Section - Payment Method Image/Icon */}
        <div className="flex items-center justify-center transition-all duration-300 group-hover:scale-105">
          {config.image ? (
            <img 
              src={config.image} 
              alt={config.name} 
              className="h-16 w-auto max-w-full object-contain transition-transform duration-300" 
            />
          ) : (
            <div className={`flex h-16 w-16 items-center justify-center rounded-xl text-white shadow-lg ${config.color}`}>
              <IconComponent size={32} />
            </div>
          )}
        </div>

        {/* Middle Section - Payment Method Name */}
        <h3 className={`text-lg font-bold transition-colors duration-300 text-center ${
          isSelected 
            ? "text-white" 
            : "text-gray-900 dark:text-gray-100 group-hover:text-primary dark:group-hover:text-primary-light"
        }`}>
          {trans(config.name)}
        </h3>

        {/* Bottom Section - Description */}
        <p className={`text-sm transition-colors duration-300 text-center leading-relaxed ${
          isSelected 
            ? "text-white/90" 
            : "text-gray-600 dark:text-gray-300"
        }`}>
          {trans(config.description)}
        </p>
      </div>

      {/* Bottom Accent Line */}
      {isSelected && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-white/40 via-white/60 to-white/40"></div>
      )}
    </button>
  );
};
